import { useNavigate } from 'react-router-dom';
import { Paper, Title, Timeline, Text, Group, Badge, ScrollArea, Stack, Button, Loader, Center } from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconUserPlus,
  IconUserMinus,
  IconTrash,
  IconClipboardCheck,
  IconExternalLink,
  IconMapPin,
  IconRefresh,
  IconNote,
} from '@tabler/icons-react';
import { formatDistanceToNow } from 'date-fns';
import { useRecentActivity } from '@/hooks/api/useAuditLogs';
import { useOrganization } from '@/hooks/useOrganization';

interface ActivityFeedProps {
  limit?: number;
}

export function ActivityFeed({ limit = 10 }: ActivityFeedProps) {
  const navigate = useNavigate();
  const { organizationId } = useOrganization();
  const { data: activities = [], isLoading } = useRecentActivity(organizationId || '', limit);

  const getActivityIcon = (actionType: string) => {
    switch (actionType) {
      case 'CREATE':
        return <IconPlus size={16} />;
      case 'UPDATE':
        return <IconEdit size={16} />;
      case 'ASSIGN':
        return <IconUserPlus size={16} />;
      case 'UNASSIGN':
        return <IconUserMinus size={16} />;
      case 'DELETE':
        return <IconTrash size={16} />;
      case 'AUDIT':
        return <IconClipboardCheck size={16} />;
      case 'LOCATION_CHANGE':
        return <IconMapPin size={16} />;
      case 'STATUS_CHANGE':
        return <IconRefresh size={16} />;
      case 'NOTE':
        return <IconNote size={16} />;
      default:
        return <IconEdit size={16} />;
    }
  };

  const getActivityColor = (actionType: string) => {
    switch (actionType) {
      case 'CREATE':
        return 'green';
      case 'UPDATE':
        return 'blue';
      case 'ASSIGN':
        return 'teal';
      case 'UNASSIGN':
        return 'orange';
      case 'DELETE':
        return 'red';
      case 'AUDIT':
        return 'grape';
      case 'LOCATION_CHANGE':
        return 'cyan';
      case 'STATUS_CHANGE':
        return 'yellow';
      case 'NOTE':
        return 'gray';
      default:
        return 'blue';
    }
  };

  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="md">
          <Title order={3}>Recent Activity</Title>
        </Group>
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      </Paper>
    );
  }

  if (activities.length === 0) {
    return (
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="md">
          <Title order={3}>Recent Activity</Title>
          <Button
            variant="subtle"
            size="xs"
            rightSection={<IconExternalLink size={14} />}
            onClick={() => navigate('/activity-log')}
          >
            View All
          </Button>
        </Group>
        <Text c="dimmed" ta="center" py="xl">
          No recent activity to display
        </Text>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" mb="md">
        <Title order={3}>Recent Activity</Title>
        <Button
          variant="subtle"
          size="xs"
          rightSection={<IconExternalLink size={14} />}
          onClick={() => navigate('/activity-log')}
        >
          View All
        </Button>
      </Group>
      <ScrollArea h={400}>
        <Timeline bulletSize={24} lineWidth={2}>
          {activities.map((activity) => (
            <Timeline.Item
              key={activity.id}
              bullet={getActivityIcon(activity.action_type)}
              color={getActivityColor(activity.action_type)}
            >
              <Stack gap={4}>
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={500} lineClamp={1}>
                    {activity.change_summary}
                  </Text>
                </Group>
                <Group gap="xs">
                  <Badge size="xs" variant="light" color={getActivityColor(activity.action_type)}>
                    {activity.action_type.replace('_', ' ')}
                  </Badge>
                  {activity.user_name && activity.user_name !== 'System' && (
                    <Text size="xs" c="dimmed">
                      by {activity.user_name}
                    </Text>
                  )}
                </Group>
                <Text size="xs" c="dimmed">
                  {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                </Text>
              </Stack>
            </Timeline.Item>
          ))}
        </Timeline>
      </ScrollArea>
    </Paper>
  );
}
